import type { UploadTelemetry } from "./ports";
import { type FamilyAwareMultipartUploadTarget, type MultipartUploadTarget, redactUploadTarget } from "./targetInitiation";

export type UploadFailureLogDetails = {
	errorKind: "error" | "non-error";
	httpStatus?: number;
};

export type UploadTelemetryLevel = "info" | "warn" | "error";

/**
 * The redacted, sink-ready shape of one upload telemetry event.
 *
 * Per-event formatting is driven from this metadata (the `level`), never from a list of event
 * names: every event routed through {@link createUploadTelemetry} is an upload event by
 * construction, so an event type added tomorrow is formatted and redacted exactly like every
 * existing one instead of failing open until someone remembers to extend a list.
 */
export type UploadTelemetryRecord =
	| { level: "info" | "warn"; event: string; context?: Record<string, unknown> }
	| { level: "error"; event: string; context?: Record<string, unknown>; error: Error };

/** Receives records that are already redacted; a sink never sees raw upload context. */
export type UploadTelemetrySink = (record: UploadTelemetryRecord) => void;

const SENSITIVE_CONTEXT_KEY =
	/(?:authorization|credential|header|secret|signature|token|url|uri|cookie|password|presigned|bucket|s3key|objectkey)/i;
const SENSITIVE_CONTEXT_VALUE =
	/(?:https?:\/\/|authorization\s*:|bearer\s+|x-amz-(?:credential|security-token|signature|signedheaders)|x-goog-(?:credential|signature)|aws4-hmac-sha256)/i;
/**
 * LSP-18217: an owner/conversation identity is an authorization boundary, not a display value.
 * Matches `ownerId`, `expectedOwnerId`, `conversationId`, … but never `uploadId`/`sessionId`,
 * which are correlation-only and are retained verbatim as they always have been.
 */
const IDENTITY_CONTEXT_KEY = /(?:^|[a-z])(?:owner|conversation)id$/i;
const IDENTITY_VALUE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NIL_IDENTITY_VALUE = "00000000-0000-0000-0000-000000000000";
/** A field longer than any legitimate correlation value is reported by length, never by content. */
const MAX_CONTEXT_STRING_LENGTH = 512;
/**
 * LSP-19422: an error class name reaches the log line verbatim, so it is admitted only when it is
 * a plain class identifier — never a header fragment, credential, or reflected server text.
 */
const SAFE_ERROR_NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/;
const UPLOAD_EVENT_PREFIX = "[Upload] ";

/** LSP-18217: a persisted identity is logged only when it passes validation. */
export function isValidUploadIdentityValue(value: unknown): value is string {
	return typeof value === "string" && IDENTITY_VALUE_PATTERN.test(value) && value.toLowerCase() !== NIL_IDENTITY_VALUE;
}

/**
 * Browser storage is untyped at runtime, so a persisted identity may not even be a string.
 * Diagnostics report only its length so a corrupt value is never echoed into the logs.
 */
export function uploadIdentityValueLength(value: unknown): number | undefined {
	return typeof value === "string" ? value.length : undefined;
}

export function redactUploadLogEvent(event: string): string {
	return SENSITIVE_CONTEXT_VALUE.test(event) ? "[Upload] upload event" : event;
}

export function redactUploadLogText(value: string | undefined): string | undefined {
	return value !== undefined && SENSITIVE_CONTEXT_VALUE.test(value) ? "[redacted]" : value;
}

function redactUploadTargetContextValue(value: unknown): unknown {
	try {
		if (!value || typeof value !== "object" || Array.isArray(value) || value instanceof Error) return undefined;
		const candidate = value as { type?: unknown; chunkUrls?: unknown; envelopes?: unknown };
		if (candidate.type !== "multipart") return undefined;
		if (!Array.isArray(candidate.chunkUrls) && !Array.isArray(candidate.envelopes)) return undefined;
		return redactUploadTarget(value as MultipartUploadTarget | FamilyAwareMultipartUploadTarget);
	} catch {
		// A hostile getter or an unparseable target stays out of the logs entirely.
		return undefined;
	}
}

/**
 * Retains small, scalar correlation fields while refusing reflected credentials, storage
 * coordinates, URL values, and object graphs that browser/telemetry serializers could traverse
 * unexpectedly. The policy is default-deny by field shape, so a field name introduced by a future
 * event is refused unless it is provably a safe scalar.
 */
export function sanitizeUploadLogContext(context: unknown): Record<string, unknown> | undefined {
	if (!context || typeof context !== "object" || context instanceof Error || Array.isArray(context)) return undefined;
	const safeContext: Record<string, unknown> = {};
	let keys: string[];
	try {
		keys = Object.keys(context);
	} catch {
		return safeContext;
	}
	for (const key of keys) {
		if (SENSITIVE_CONTEXT_KEY.test(key)) continue;
		let value: unknown;
		try {
			value = (context as Record<string, unknown>)[key];
		} catch {
			continue;
		}
		if (IDENTITY_CONTEXT_KEY.test(key)) {
			if (isValidUploadIdentityValue(value)) {
				safeContext[key] = value;
			} else {
				const length = uploadIdentityValueLength(value);
				if (length !== undefined) safeContext[`${key}Length`] = length;
			}
		} else if (typeof value === "string") {
			if (SENSITIVE_CONTEXT_VALUE.test(value)) continue;
			if (value.length > MAX_CONTEXT_STRING_LENGTH) safeContext[`${key}Length`] = value.length;
			else safeContext[key] = value;
		} else if (typeof value === "number" || typeof value === "boolean" || value === null) {
			safeContext[key] = value;
		} else {
			const redactedTarget = redactUploadTargetContextValue(value);
			if (redactedTarget !== undefined) safeContext[key] = redactedTarget;
		}
	}
	return safeContext;
}

export function uploadContextError(context: unknown): unknown {
	if (!context || typeof context !== "object" || context instanceof Error || Array.isArray(context)) return context;
	try {
		return (context as Record<string, unknown>).error;
	} catch {
		return undefined;
	}
}

/** Keeps signed request details and reflected error text out of browser/Datadog logs. */
export function uploadFailureLogDetails(error: unknown): UploadFailureLogDetails {
	const details: UploadFailureLogDetails = { errorKind: error instanceof Error ? "error" : "non-error" };
	try {
		const status = (error as { status?: unknown } | null | undefined)?.status;
		if (typeof status === "number" && Number.isSafeInteger(status) && status >= 100 && status <= 599) details.httpStatus = status;
	} catch {
		// A hostile getter must not replace the original upload outcome.
	}
	return details;
}

/** A facade refusal code (`UPLOAD_NOT_FOUND`, `UPLOAD_OPERATION_IN_PROGRESS`, …), never prose. */
const SAFE_SERVER_REFUSAL_CODE = /^[A-Z][A-Z0-9_]{1,63}$/;

function readSafeHttpStatus(value: unknown): number | undefined {
	return typeof value === "number" && Number.isSafeInteger(value) && value >= 100 && value <= 599 ? value : undefined;
}

function readSafeRefusalCode(value: unknown): string | undefined {
	return typeof value === "string" && SAFE_SERVER_REFUSAL_CODE.test(value) ? value : undefined;
}

/**
 * The server's verdict on a refused upload request — its HTTP status and typed `errorCode` — read
 * from either rejection shape the hosts produce. contact-admin's `api._handleResponse` rejects with
 * the parsed JSON body (`httpStatus` number, `errorCode` string, `status: "error"`), which is not an
 * Error and so is invisible to {@link uploadFailureLogDetails}; the shared uploads facade client
 * throws an Error with `status` and a string `code`. The code is admitted only as a bare
 * UPPER_SNAKE token so a reflected body can never ride it into the log.
 */
export function uploadServerRefusalLogDetails(error: unknown): { httpStatus?: number; errorCode?: string } {
	const details: { httpStatus?: number; errorCode?: string } = {};
	if (!error || typeof error !== "object") return details;
	const candidate = error as { status?: unknown; httpStatus?: unknown; errorCode?: unknown; code?: unknown };
	try {
		const httpStatus = readSafeHttpStatus(candidate.status) ?? readSafeHttpStatus(candidate.httpStatus);
		if (httpStatus !== undefined) details.httpStatus = httpStatus;
	} catch {
		// A hostile getter loses the status, never the upload outcome.
	}
	try {
		const errorCode = readSafeRefusalCode(candidate.errorCode) ?? readSafeRefusalCode(candidate.code);
		if (errorCode !== undefined) details.errorCode = errorCode;
	} catch {
		// Same containment as the status read above.
	}
	return details;
}

/**
 * The originating class name, when it is provably a bare identifier. Retained because it is the
 * one field that separates a deliberate deferral from a terminal failure at a glance.
 */
function safeUploadErrorName(error: unknown): string | undefined {
	try {
		const name = (error as { name?: unknown } | null | undefined)?.name;
		return typeof name === "string" && SAFE_ERROR_NAME.test(name) ? name : undefined;
	} catch {
		// A hostile getter loses its class name rather than the whole failure record.
		return undefined;
	}
}

/**
 * LSP-19422: the marker an error sets to declare its own message engine-authored — prose plus
 * correlation ids and a bracketed facade code, never a reflected response body. Declared at the
 * construction site because that is the only place that knows which it is: a reflected S3 error
 * body carries `<Key>`/`<BucketName>`, matches none of the value patterns above, and sits well
 * under the length cap, so safety cannot be inferred from the string's shape.
 *
 * It is a SYMBOL, not a string key, because not every rejected value reaching this boundary is an
 * engine error. `api._handleResponse` rejects with the raw parsed server JSON body, and the resume
 * path logs that value unnormalized (`decidePersistedTerminal` → `releasePersistedSessionForRetry`
 * → `logError("recording.fileUpload.resumeFailed", …, failure.error)`). A string-keyed flag would
 * therefore be settable by the very server whose reflected text this boundary exists to refuse.
 * JSON cannot express a symbol key, so a parsed body can never carry this marker.
 */
export const UPLOAD_REASON_SAFE: unique symbol = Symbol("uploadReasonSafe");

export type UploadReasonSafeError = Error & { readonly [UPLOAD_REASON_SAFE]: true };

/**
 * Two independent gates, each sufficient on its own: a parsed server body is not an `Error`, and it
 * cannot hold a symbol key even if a future path wraps it in one via `Object.assign`. Undeclared
 * errors keep their class name and take the generic reason, so a class added tomorrow is
 * generic-by-default instead of leaking by default.
 */
function declaresSafeUploadReason(error: unknown): boolean {
	try {
		return error instanceof Error && (error as Partial<UploadReasonSafeError>)[UPLOAD_REASON_SAFE] === true;
	} catch {
		// A hostile getter cannot opt itself in.
		return false;
	}
}

/**
 * The originating reason, admitted only for a declared engine-authored message and then held to the
 * same value policy as any other logged string: refused when it reflects a signed URL or credential,
 * and refused when it is longer than a legitimate reason, so an unbounded server-supplied facade
 * code cannot turn a declared message into reflected error text.
 */
function safeUploadErrorReason(error: unknown): string | undefined {
	if (!declaresSafeUploadReason(error)) return undefined;
	let message: unknown;
	try {
		message = (error as { message?: unknown } | null | undefined)?.message;
	} catch {
		// A hostile getter falls back to the caller's generic label.
		return undefined;
	}
	if (typeof message !== "string" || message.length === 0 || message.length > MAX_CONTEXT_STRING_LENGTH) return undefined;
	const redacted = redactUploadLogText(message);
	return redacted === "[redacted]" ? undefined : redacted;
}

/**
 * LSP-19422: the class survives to the log line for every failure, and a declared engine-authored
 * reason survives with it, so a deferral with a kept-pending session is distinguishable from a
 * terminal failure without reading the surrounding fields. `message` is the fallback label used
 * when no reason is admitted — not an override.
 */
export function redactedUploadFailure(error: unknown, message = "Upload operation failed"): Error {
	const redacted = new Error(safeUploadErrorReason(error) ?? message);
	const name = safeUploadErrorName(error);
	if (name !== undefined) redacted.name = name;
	Object.assign(redacted, uploadFailureLogDetails(error));
	return redacted;
}

/**
 * Error-level upload events carry the console-allowlist prefix the test harness and log filters
 * key off. The decision reads the record's `level`, so a new event type is prefixed by default.
 */
function formatUploadTelemetryEvent(level: UploadTelemetryLevel, event: string): string {
	const redactedEvent = redactUploadLogEvent(event);
	if (level !== "error" || redactedEvent.startsWith(UPLOAD_EVENT_PREFIX)) return redactedEvent;
	return `${UPLOAD_EVENT_PREFIX}${redactedEvent}`;
}

/** Builds the redacted record a sink receives. Exported so redaction is testable without a sink. */
export function buildUploadTelemetryRecord(
	level: UploadTelemetryLevel,
	event: string,
	context?: unknown,
	error?: unknown
): UploadTelemetryRecord {
	const safeContext = sanitizeUploadLogContext(context);
	const formattedEvent = formatUploadTelemetryEvent(level, event);
	if (level === "error") {
		return {
			level,
			event: formattedEvent,
			context: safeContext,
			error: redactedUploadFailure(error ?? uploadContextError(context))
		};
	}
	return {
		level,
		event: formattedEvent,
		context: error === undefined ? safeContext : { ...safeContext, ...uploadFailureLogDetails(error) }
	};
}

/**
 * The single upload-telemetry boundary for every app. Redaction happens here, before the sink, so
 * a host that forwards straight to console/Datadog cannot leak signed URLs, storage coordinates,
 * or reflected error text — the defect async's raw pass-through adapter shipped.
 */
export function createUploadTelemetry(sink: UploadTelemetrySink): UploadTelemetry {
	const emit = (level: UploadTelemetryLevel, event: string, context?: unknown, error?: unknown): void => {
		try {
			sink(buildUploadTelemetryRecord(level, event, context, error));
		} catch {
			// Telemetry is never allowed to alter upload control flow.
		}
	};
	return {
		// Info is a breadcrumb level: an error argument is deliberately not folded in here.
		logInfo: (event, context) => emit("info", event, context),
		logWarn: (event, context, error) => emit("warn", event, context, error),
		logError: (event, context, error) => emit("error", event, context, error)
	};
}
